import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import {
  BarChartOutlined,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
  MoreVert,
  Search,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import DownloadDataButton from "../DownloadDataButton";
import { ACTIONS_COLUMN_ID } from "../../../constants/dashboardConstants";
import { usePermission } from "../../../hooks/usePermission";
import "./DynamicTable.css";

const getHeaderKey = (header) => header.key || header.id || header.value || header.label;
const getHeaderLabel = (header) => {
  if (header.label != null) return header.label;
  return header.value || header.key || "";
};

const getNestedValue = (row, path) => {
  if (!path) return "";
  return String(path)
    .split(".")
    .reduce((value, key) => (value == null ? value : value[key]), row);
};

const isReactNode = (value) => React.isValidElement(value);

const getRowSearchText = (row, headers) =>
  headers
    .map((header) => {
      const value = getNestedValue(row, getHeaderKey(header));
      if (value == null || isReactNode(value)) return "";
      if (typeof value === "object") return JSON.stringify(value);
      return String(value);
    })
    .join(" ")
    .toLowerCase();

const getPageNumbers = (page, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }
  if (page <= 3) return [1, 2, 3, "...", totalPages];
  if (page >= totalPages - 2) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", page, "...", totalPages];
};

function DefaultActionIcon({ action }) {
  if (!action) return null;

  if (typeof action.icon === "string") {
    return (
      <img
        src={action.icon}
        alt={action.label || action.id || "action icon"}
        style={{ width: 16, height: 16, display: "block" }}
      />
    );
  }

  const Icon = action.icon || (action.id === "analytics" ? BarChartOutlined : MoreVert);
  return Icon ? <Icon sx={{ fontSize: 16 }} /> : null;
}

export function TableStatusBadge({ children, tone = "green" }) {
  return <span className={`new-dynamic-table-status ${tone}`}>{children}</span>;
}

export function TableLastUpdate({ children, tone = "green" }) {
  return (
    <span className="new-dynamic-table-last-update">
      <i className={tone} />
      {children}
    </span>
  );
}

const renderDefaultCell = (value) => {
  if (value == null || value === "") return "--";
  return value;
};

export default function DynamicTable({
  title = "All Routes",
  headers = [],
  data = [],
  filters = [],
  actions = [],
  actionHeader = "Actions",
  rowKey = "id",
  searchPlaceholder = "Search routes",
  searchValue,
  onSearchChange,
  showSearch = true,
  loading = false,
  emptyMessage = "No data available",
  pagination = {},
  className = "",
  minWidth = 940,
  onRowClick,
  renderActions,
  renderExpandedRow,
  headerVariant = "light",
  tabs = [],
  activeTab,
  onTabChange,
  toolbarStart,
  toolbarEnd,
  showDownloadButton = true,
  downloadData,
  downloadHeaders,
  downloadFileName,
  onDownload,
  selectable = false,
  selectedRowKeys = [],
  onSelectionChange,
  showTotalCount = true,
}) {
  const { t } = useTranslation();
  const { hasDownloadPermission } = usePermission();
  const [internalSearch, setInternalSearch] = useState("");
  const [internalPage, setInternalPage] = useState(1);
  const [visibleCount, setVisibleCount] = useState(pagination.pageSize || 5);
  const [internalActiveTab, setInternalActiveTab] = useState(tabs[0]?.value || tabs[0]?.label || "");

  const moduleTarget = downloadFileName || title || "";
  const canUserDownload = showDownloadButton !== false && hasDownloadPermission(moduleTarget);
  const isSelectable = Boolean(selectable && canUserDownload);

  const currentSearch = searchValue ?? internalSearch;
  const currentTab = activeTab ?? internalActiveTab;
  const pageSize = pagination.pageSize || 5;
  const controlledPage = pagination.page;
  const page = controlledPage || internalPage;
  const enablePagination = pagination.enabled !== false;
  const clientPagination = pagination.client !== false;

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "none",
  });
  const showMoreMode = pagination.mode === "showMore";

  const searchedRows = useMemo(() => {
    const query = String(currentSearch || "").trim().toLowerCase();
    if (!query || onSearchChange) return data;
    return data.filter((row) => getRowSearchText(row, headers).includes(query));
  }, [currentSearch, data, headers, onSearchChange]);

  const handleHeaderSort = (headerKey) => {
    if (
      !headerKey ||
      headerKey === "actions" ||
      headerKey === "Actions" ||
      headerKey === ACTIONS_COLUMN_ID
    ) {
      return;
    }

    setSortConfig((prev) => {
      if (prev.key !== headerKey) return { key: headerKey, direction: "asc" };
      if (prev.direction === "asc") return { key: headerKey, direction: "desc" };
      if (prev.direction === "desc") return { key: headerKey, direction: "none" };
      return { key: headerKey, direction: "asc" };
    });
  };

  const sortedRows = useMemo(() => {
    const sourceRows = searchedRows;
    if (!sourceRows.length) return [];
    if (!sortConfig.key || sortConfig.direction === "none") return sourceRows;

    return [...sourceRows].sort((a, b) => {
      let aVal = getNestedValue(a, sortConfig.key);
      let bVal = getNestedValue(b, sortConfig.key);

      if (isReactNode(aVal)) {
        aVal = aVal.props?.alt || aVal.props?.title || aVal.props?.children || "";
      }
      if (isReactNode(bVal)) {
        bVal = bVal.props?.alt || bVal.props?.title || bVal.props?.children || "";
      }

      if (aVal == null) aVal = "";
      if (bVal == null) bVal = "";

      const aStr = String(aVal).trim().toLowerCase();
      const bStr = String(bVal).trim().toLowerCase();

      const aNum = parseFloat(aStr);
      const bNum = parseFloat(bStr);
      if (!isNaN(aNum) && !isNaN(bNum) && String(aNum) === aStr && String(bNum) === bStr) {
        return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
      }

      return sortConfig.direction === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });
  }, [searchedRows, sortConfig]);

  const totalItems = pagination.totalItems ?? sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);

  const visibleRows = useMemo(() => {
    if (!enablePagination || !clientPagination) return sortedRows;
    if (showMoreMode) return sortedRows.slice(0, visibleCount);
    const start = (safePage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [clientPagination, enablePagination, pageSize, safePage, sortedRows, showMoreMode, visibleCount]);

  useEffect(() => {
    if (showMoreMode) setVisibleCount(pageSize);
  }, [data, pageSize, showMoreMode]);

  const showActions = Boolean(renderActions || actions.length);
  const startItem = totalItems === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const endItem = Math.min(safePage * pageSize, totalItems);
  const pageNumbers = getPageNumbers(safePage, totalPages);
  const itemLabel = pagination.itemLabel || "vehicles";

  const getKey = useCallback(
    (row, index) => {
      if (typeof rowKey === "function") return rowKey(row, index);
      return getNestedValue(row, rowKey) || row.id || row.key || index;
    },
    [rowKey],
  );

  const exportHeaders = downloadHeaders || headers;
  const exportData = useMemo(() => {
    let rawData = downloadData || sortedRows;
    if (isSelectable && selectedRowKeys && selectedRowKeys.length > 0) {
      rawData = rawData.filter((row, idx) => {
        const k = getKey(row, idx);
        return selectedRowKeys.some((sk) => String(sk) === String(k));
      });
    }
    const query = String(currentSearch || "").trim().toLowerCase();
    if (!query || onSearchChange) return rawData;
    return rawData.filter((row) => getRowSearchText(row, exportHeaders).includes(query));
  }, [downloadData, sortedRows, isSelectable, selectedRowKeys, getKey, currentSearch, onSearchChange, exportHeaders]);
  const fileName = downloadFileName || title || "export-data";

  const allSelectableRows = useMemo(() => {
    return downloadData || sortedRows;
  }, [downloadData, sortedRows]);

  const allSelectableKeys = useMemo(() => {
    return allSelectableRows.map((r, i) => getKey(r, i));
  }, [allSelectableRows, getKey]);

  const isAllSelected =
    allSelectableKeys.length > 0 &&
    allSelectableKeys.every((k) => selectedRowKeys.some((sk) => String(sk) === String(k)));

  const isIndeterminate =
    selectedRowKeys.length > 0 &&
    !isAllSelected &&
    allSelectableKeys.some((k) => selectedRowKeys.some((sk) => String(sk) === String(k)));

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setInternalPage(1);
    setVisibleCount(pageSize);
    if (onSearchChange) {
      onSearchChange(value);
      return;
    }
    setInternalSearch(value);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages) return;
    if (pagination.onPageChange) {
      pagination.onPageChange(nextPage);
      return;
    }
    setInternalPage(nextPage);
  };

  const handleTabChange = (tab) => {
    const value = tab.value || tab.label;
    setInternalPage(1);
    if (onTabChange) {
      onTabChange(value);
      return;
    }
    setInternalActiveTab(value);
  };

  const renderCell = (row, header, rowIndex) => {
    if (header.render) return header.render(row, rowIndex);
    return renderDefaultCell(getNestedValue(row, getHeaderKey(header)));
  };

  const displayTitle = useMemo(() => {
    if (!title) return null;
    if (typeof title !== "string") return title;
    if (!showTotalCount) return t(title);

    // If the caller already included parentheses with counts, e.g. "All Routes (25 Routes)"
    if (/\(\s*\d+[^)]*\)/.test(title)) {
      return t(title);
    }

    const count = (downloadData || sortedRows).length;
    return `${t(title)} (${count} ${t("Total")})`;
  }, [title, downloadData, sortedRows, showTotalCount, t]);

  return (
    <section className={`new-dynamic-table-card ${headerVariant === "dark" ? "dark-header" : ""} ${className}`}>
      <div className="new-dynamic-table-toolbar">
        {displayTitle ? <h3>{displayTitle}</h3> : null}

        <div className="new-dynamic-table-controls">
          {toolbarStart}
          {showSearch && (
            <TextField
              className="new-dynamic-table-search"
              value={currentSearch}
              onChange={handleSearchChange}
              placeholder={t(searchPlaceholder)}
              size="small"
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 15 }} />
                  </InputAdornment>
                ),
              }}
            />
          )}

          {filters.map((filter) => (
            <div
              className={`new-dynamic-table-filter ${filter.value ? "selected" : ""}`}
              key={filter.key || filter.label}
            >
              <Select
                value={filter.value}
                onChange={(event) => filter.onChange?.(event.target.value)}
                aria-label={t(filter.label || filter.key || "Filter")}
                size="small"
                displayEmpty
                IconComponent={KeyboardArrowDown}
              >
                {(filter.options || []).map((option) => {
                  const optionValue = typeof option === "object" ? option.value : option;
                  const optionLabel = typeof option === "object" ? option.label : option;
                  return (
                    <MenuItem key={optionValue} value={optionValue}>
                      {t(optionLabel)}
                    </MenuItem>
                  );
                })}
              </Select>
            </div>
          ))}
          {toolbarEnd}
          {isSelectable && selectedRowKeys.length > 0 && (
            <div
              style={{
                fontSize: "12px",
                fontWeight: 600,
                color: "#0284c7",
                background: "#e0f2fe",
                padding: "3px 8px",
                borderRadius: "12px",
                whiteSpace: "nowrap",
                display: "inline-flex",
                alignItems: "center",
              }}
            >
              {selectedRowKeys.length} {t("selected")}
            </div>
          )}
          {canUserDownload && (
            <DownloadDataButton
              headers={exportHeaders}
              data={exportData}
              onDownload={onDownload}
              filename={fileName}
              disabled={isSelectable && selectedRowKeys.length === 0}
              tooltipTitle={
                isSelectable && selectedRowKeys.length === 0
                  ? t("Please select records to download")
                  : selectedRowKeys.length > 0
                  ? `${t("Download")} (${selectedRowKeys.length})`
                  : t("Download")
              }
            />
          )}
        </div>
      </div>

      {tabs.length > 0 && (
        <div className="new-dynamic-table-tabs">
          {tabs.map((tab) => {
            const value = tab.value || tab.label;
            return (
              <button
                key={value}
                type="button"
                className={currentTab === value ? "active" : ""}
                onClick={() => handleTabChange(tab)}
              >
                {t(tab.label || value)}
              </button>
            );
          })}
        </div>
      )}

      <div className="new-dynamic-table-scroll">
        <table style={{ minWidth }}>
          <thead>
            <tr>
              {isSelectable && (
                <th
                  className="new-dynamic-table-select-head"
                  style={{
                    width: 44,
                    minWidth: 44,
                    maxWidth: 44,
                    padding: "0 4px",
                    textAlign: "center",
                  }}
                >
                  <Checkbox
                    size="small"
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={(e) => {
                      if (e.target.checked) {
                        onSelectionChange?.(allSelectableKeys);
                      } else {
                        onSelectionChange?.([]);
                      }
                    }}
                    sx={{
                      color: headerVariant === "dark" ? "#ffffff" : "#9CA3AF",
                      "&.Mui-checked, &.MuiCheckbox-indeterminate": {
                        color: headerVariant === "dark" ? "#60a5fa" : "#20A8E0",
                      },
                      padding: "4px",
                    }}
                  />
                </th>
              )}
              {headers.map((header) => {
                const hk = getHeaderKey(header);
                const isSortable = hk !== "actions" && hk !== "Actions" && hk !== ACTIONS_COLUMN_ID;
                const isCurrentSort = sortConfig.key === hk;
                return (
                  <th
                    key={hk}
                    onClick={isSortable ? () => handleHeaderSort(hk) : undefined}
                    style={{
                      width: header.width,
                      textAlign: header.align || "left",
                      cursor: isSortable ? "pointer" : "default",
                      userSelect: "none",
                    }}
                  >
                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                      {t(getHeaderLabel(header))}
                      {isSortable && (
                        <span style={{ fontSize: 10, opacity: isCurrentSort ? 1 : 0.4 }}>
                          {isCurrentSort && sortConfig.direction === "asc"
                            ? "▲"
                            : isCurrentSort && sortConfig.direction === "desc"
                            ? "▼"
                            : "⇅"}
                        </span>
                      )}
                    </div>
                  </th>
                );
              })}
              {showActions && <th className="new-dynamic-table-actions-head">{t(actionHeader)}</th>}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              Array.from({ length: pageSize }).map((_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`}>
                  {isSelectable && (
                    <td style={{ width: 44, textAlign: "center" }}>
                      <span className="new-dynamic-table-skeleton short" />
                    </td>
                  )}
                  {headers.map((header) => (
                    <td key={getHeaderKey(header)}>
                      <span className="new-dynamic-table-skeleton" />
                    </td>
                  ))}
                  {showActions && (
                    <td>
                      <span className="new-dynamic-table-skeleton short" />
                    </td>
                  )}
                </tr>
              ))
            ) : visibleRows.length ? (
              visibleRows.map((row, rowIndex) => {
                const expandedContent = renderExpandedRow?.(row, rowIndex);

                return (
                  <React.Fragment key={getKey(row, rowIndex)}>
                    <tr
                      onClick={onRowClick ? () => onRowClick(row, rowIndex) : undefined}
                      className={onRowClick ? "clickable" : ""}
                    >
                      {isSelectable && (() => {
                        const rowKeyVal = getKey(row, rowIndex);
                        const isChecked = selectedRowKeys.some((k) => String(k) === String(rowKeyVal));
                        return (
                          <td
                            className="new-dynamic-table-select-cell"
                            style={{
                              width: 44,
                              minWidth: 44,
                              maxWidth: 44,
                              padding: "0 4px",
                              textAlign: "center",
                            }}
                            onClick={(event) => event.stopPropagation()}
                          >
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={(event) => {
                                event.stopPropagation();
                                const next = isChecked
                                  ? selectedRowKeys.filter((k) => String(k) !== String(rowKeyVal))
                                  : [...selectedRowKeys, rowKeyVal];
                                onSelectionChange?.(next);
                              }}
                              sx={{
                                color: "#9CA3AF",
                                "&.Mui-checked": {
                                  color: "#20A8E0",
                                },
                                padding: "4px",
                              }}
                            />
                          </td>
                        );
                      })()}
                      {headers.map((header) => (
                        <td key={getHeaderKey(header)} style={{ textAlign: header.align || "left" }}>
                          {renderCell(row, header, rowIndex)}
                        </td>
                      ))}

                      {showActions && (
                        <td className="new-dynamic-table-actions-cell">
                          {renderActions ? (
                            renderActions(row, rowIndex)
                          ) : (
                            <div className="new-dynamic-table-actions">
                              {actions.map((action) => (
                                <button
                                  key={action.id || action.label}
                                  type="button"
                                  title={t(action.label || action.id)}
                                  aria-label={t(action.label || action.id)}
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    action.onClick?.(row, rowIndex, event);
                                  }}
                                >
                                  {action.render ? (
                                    action.render(row, rowIndex)
                                  ) : (
                                    <DefaultActionIcon action={action} />
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                    {expandedContent ? (
                      <tr className="new-dynamic-table-expanded-row">
                        <td colSpan={headers.length + (showActions ? 1 : 0) + (isSelectable ? 1 : 0)}>
                          {expandedContent}
                        </td>
                      </tr>
                    ) : null}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={headers.length + (showActions ? 1 : 0) + (isSelectable ? 1 : 0)}
                  className="new-dynamic-table-empty"
                >
                  {t(emptyMessage)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {enablePagination && showMoreMode && (
        <div className="new-dynamic-table-footer show-more-footer">
          <span>
            {pagination.summary ||
              `${t("Showing")} ${visibleRows.length ? 1 : 0} ${t("to")} ${visibleRows.length} ${t("of")} ${totalItems} ${t(itemLabel)}`}
          </span>
          {visibleRows.length < searchedRows.length && (
            <button
              type="button"
              className="new-dynamic-table-show-more"
              onClick={() => setVisibleCount((count) => count + pageSize)}
            >
              {t("Show more")}
            </button>
          )}
        </div>
      )}

      {enablePagination && !showMoreMode && (
        <div className="new-dynamic-table-footer">
          <span>
            {pagination.summary ||
              `${t("Showing")} ${startItem} ${t("to")} ${endItem} ${t("of")} ${totalItems} ${t(itemLabel)}`}
          </span>

          <div className="new-dynamic-table-pagination">
            <button
              type="button"
              onClick={() => handlePageChange(safePage - 1)}
              disabled={safePage === 1}
              aria-label={t("Previous page")}
            >
              <ChevronLeft sx={{ fontSize: 14 }} />
            </button>

            {pageNumbers.map((pageNumber, index) =>
              pageNumber === "..." ? (
                <span key={`ellipsis-${index}`}>...</span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  className={pageNumber === safePage ? "active" : ""}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              )
            )}

            <button
              type="button"
              onClick={() => handlePageChange(safePage + 1)}
              disabled={safePage === totalPages}
              aria-label={t("Next page")}
            >
              <ChevronRight sx={{ fontSize: 14 }} />
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
